/**
 * Quem acessa cada tela (padrão P-S1, src/docs/padroes-front.md; ondas O0 e O0c).
 *
 * Fonte única para as ROTAS (GuardaPerfil), o MENU (BarraSuperiorEsquerda) e os BOTÕES (usePermissao).
 * Cada rota aponta para uma ROTINA do catálogo do back (4clinics_backend/funcs/auth/catalogoRotinas.js); o nível
 * de cada rotina vem do back no login e no /me (`permissoes`), conforme o perfil do usuário na empresa — o
 * administrador configura em "Perfis e permissões". Aqui é só o reflexo na interface: o back aplica as mesmas
 * permissões em toda chamada. O teste de contrato do back confere que toda `rotina` daqui existe no catálogo.
 *
 * `acesso` = { perfil, permissoes, acessoClinicoTotal } (do useAuth): perfil é a BASE (administrador · recepcao ·
 * profissional); acessoClinicoTotal = permissão Admin Clínico (F2.1), que não é rotina: é do usuário.
 */

export const PERFIS = Object.freeze({
  ADMINISTRADOR: 'administrador',
  RECEPCAO: 'recepcao',
  PROFISSIONAL: 'profissional',
});

export const ROTULO_PERFIL = Object.freeze({
  administrador: 'Administrador',
  recepcao: 'Recepção',
  profissional: 'Profissional',
});

/** Níveis por rotina (iguais aos do back). */
export const NIVEIS = Object.freeze({ SEM_ACESSO: 0, CONSULTAR: 1, ALTERAR: 2, TOTAL: 3 });

export const ROTULO_NIVEL = Object.freeze({
  0: 'Sem acesso',
  1: 'Consultar',
  2: 'Incluir e alterar',
  3: 'Total',
});

/**
 * Prefixo de rota → rotina. Vale o prefixo mais longo que casar.
 * `livre`: qualquer usuário autenticado. Rota que não casa com nenhum prefixo: só o administrador (padrão seguro).
 */
export const REGRAS_ROTA = Object.freeze([
  // Sempre liberadas
  { prefixo: '/sem-permissao', livre: true },
  { prefixo: '/definir-senha', livre: true },
  { prefixo: '/minha-conta', livre: true },
  // Operação
  // Painéis gerenciais (4clinics_backend/docs/plano-paineis-gerenciais.md): abre com qualquer painel liberado (cada aba segue a própria rotina)
  { prefixo: '/', exato: true, paineis: true },
  // V3 (docs/plano-paineis-v2.md §5): detalhe de um indicador/gráfico; o back confere a rotina do painel e a de destino (agenda, relatórios financeiros)
  { prefixo: '/paineis/detalhe', paineis: true },
  { prefixo: '/agenda', rotina: 'agenda' },
  { prefixo: '/hoje', rotina: 'atendimento' },
  { prefixo: '/atendimento', rotina: 'atendimento' },
  // F2.1: prontuário do paciente — profissional com a rotina "atendimento" ou Admin Clínico (podeVerProntuario)
  { prefixo: '/prontuario', prontuario: true },
  // F2.2: rastreabilidade do prontuário — Administrador ou Admin Clínico (podeVerLogAcesso)
  { prefixo: '/log-acesso', logAcesso: true },
  // R1: relatórios (agendas por plano) — Administrador ou perfil com a rotina "relatorios" liberada (sem exceção de Admin Clínico, D18)
  { prefixo: '/relatorios', relatorios: true },
  // Relatórios financeiros (docs/relatorios-financeiros.md): rotina própria, só Administrador por padrão; o prefixo mais longo vence o de /relatorios
  { prefixo: '/relatorios/financeiro', rotina: 'relatoriosFinanceiros' },
  // RV1 (docs/plano-pacotes-renovacao.md): pacotes e guias a renovar, com telefones; rotina própria, só Administrador por padrão
  { prefixo: '/relatorios/renovacao-pacotes', rotina: 'relatorioRenovacaoPacotes' },
  { prefixo: '/relatorios/saldo-sem-agenda', rotina: 'relatorioSaldoSemAgenda' },
  { prefixo: '/pacientes', rotina: 'pacientes' },
  { prefixo: '/pacienteEvents', rotina: 'pacientes' },
  { prefixo: '/pacienteIdFonePac', rotina: 'pacientesFones' },
  { prefixo: '/pacientesPacotes', rotina: 'pacotes' },
  { prefixo: '/carteirinhasPendentes', rotina: 'pacotes' }, // FAT2B: pacientes sem carteirinha/vencida
  // Faturamento (4clinics_backend/docs/faturamento-planos-saude.md): sem exceção de Admin Clínico (D18)
  { prefixo: '/titulosPlano', rotina: 'titulosPlano' },
  { prefixo: '/faturasPlano', rotina: 'faturasPlano' },
  { prefixo: '/motivosGlosa', rotina: 'faturasPlano' }, // FIN-B: motivos de glosa (consulta 1 · cadastra 2)
  { prefixo: '/notasPlano', rotina: 'notasPlano' }, // FIN-B: NF dos planos (consulta 1 · gera 2 · estorna 3)
  // Financeiro (docs/financeiro-contas-a-receber.md): sem exceção de Admin Clínico (D18)
  { prefixo: '/contasReceber', rotina: 'contasReceber' },
  // Configuração
  { prefixo: '/estados', rotina: 'estados' },
  { prefixo: '/cidades', rotina: 'cidades' },
  { prefixo: '/profissionais', rotina: 'profissionais' },
  { prefixo: '/profissionaisEspecIndex', rotina: 'profissionais' },
  { prefixo: '/profissionaisEspecForm', rotina: 'profissionais' },
  { prefixo: '/profTiposPagtos', rotina: 'profissionaisValores' },
  { prefixo: '/profEspecValores', rotina: 'profissionaisValores' },
  { prefixo: '/disponibilidadeSemanal', rotina: 'disponibilidade' },
  { prefixo: '/especializacoes', rotina: 'especialidades' },
  { prefixo: '/tiposConsultas', rotina: 'tiposConsultas' },
  { prefixo: '/tiposPagtosProf', rotina: 'tiposPagamento' },
  { prefixo: '/planosSaude', rotina: 'planosSaude' },
  // Recebimento do pacote particular (docs: 4clinics_backend/docs/recebimento-pacote-particular.md)
  { prefixo: '/formasRecebimento', rotina: 'formasRecebimento' },
  { prefixo: '/condicoesRecebimento', rotina: 'condicoesRecebimento' },
  { prefixo: '/tabelasPrecos', rotina: 'tabelasPrecos' },
  // Administração
  { prefixo: '/usuarios', rotina: 'usuarios' },
  { prefixo: '/perfis', rotina: 'perfis' },
  // IMP (4clinics_backend/docs/importacao-planilhas.md): importação de planilhas do sistema anterior — só o Administrador
  { prefixo: '/importacoes', rotina: 'importacaoPlanilhas' },
  // PS2 (4clinics_backend/docs/plano-parametros-sistema.md): parâmetros do sistema — só o Administrador
  { prefixo: '/parametros', rotina: 'parametrosSistema' },
]);

/**
 * Ordem de preferência da tela inicial de quem não é administrador. F1 (F-D2/F-D3): o profissional (rotina
 * "atendimento") entra na "Hoje"; a recepção (sem essa rotina) cai direto na agenda.
 */
const INICIAIS = ['/hoje', '/agenda', '/pacientes', '/pacientesPacotes', '/'];

function casa(regra, caminho) {
  if (regra.exato) return caminho === regra.prefixo;
  return caminho === regra.prefixo || caminho.startsWith(`${regra.prefixo}/`);
}

/** Regra aplicável ao caminho (prefixo mais longo), ou null. */
export function regraDaRota(caminho) {
  const limpo = String(caminho || '/').split('?')[0].replace(/\/+$/, '') || '/';
  return (
    REGRAS_ROTA.filter((r) => casa(r, limpo)).sort((a, b) => b.prefixo.length - a.prefixo.length)[0] || null
  );
}

/** Nível do usuário na rotina (administrador sem mapa → total). */
export function nivelRotina(acesso, rotina) {
  if (!acesso?.perfil || !rotina) return NIVEIS.SEM_ACESSO;
  const nivel = acesso.permissoes?.[rotina];
  if (nivel !== undefined && nivel !== null) return Number(nivel);
  return acesso.perfil === PERFIS.ADMINISTRADOR ? NIVEIS.TOTAL : NIVEIS.SEM_ACESSO;
}

/**
 * Pode abrir o prontuário (linha do tempo, apontamentos)? Reflexo da regra única do back
 * (4clinics_backend/funcs/prontuario/acessoProntuario.js): Admin Clínico, ou profissional com a rotina "atendimento".
 * O back ainda exige o vínculo com o profissional e decide apontamento por apontamento (restrito, rascunho).
 */
export function podeVerProntuario(acesso) {
  if (!acesso?.perfil) return false;
  if (acesso.acessoClinicoTotal) return true;
  return acesso.perfil === PERFIS.PROFISSIONAL && nivelRotina(acesso, 'atendimento') >= NIVEIS.CONSULTAR;
}

/** Log de acesso ao prontuário (rastreabilidade, sem conteúdo clínico): Administrador ou Admin Clínico. */
export function podeVerLogAcesso(acesso) {
  if (!acesso?.perfil) return false;
  return acesso.perfil === PERFIS.ADMINISTRADOR || Boolean(acesso.acessoClinicoTotal);
}

/**
 * Relatórios com dados de pacientes (R1): Administrador ou quem tem a rotina "relatorios" no perfil. Sem exceção de
 * Admin Clínico (D18, docs de faturamento). Reflexo da regra do back (RelatoriosController.podeVerRelatorios); o back recusa de novo.
 */
export function podeVerRelatorios(acesso) {
  if (!acesso?.perfil) return false;
  if (acesso.perfil === PERFIS.ADMINISTRADOR) return true;
  return nivelRotina(acesso, 'relatorios') >= NIVEIS.CONSULTAR;
}

/**
 * Rotinas dos painéis gerenciais (uma por painel, catálogo do back no grupo "Painéis"). O nível é o ESCOPO dos dados (D8): 1 só os meus ·
 * 2 toda a clínica. O Administrador vê sempre toda a clínica; os demais, Admin Clínico inclusive, só pelo nível no perfil (D9).
 */
export const ROTINAS_PAINEIS = Object.freeze(['painel', 'painelFaltas', 'painelOcupacao', 'painelFinanceiro', 'painelFaturamento', 'painelPacotes', 'painelPacientes', 'painelClinico']);

export function podeVerPaineis(acesso) {
  if (!acesso?.perfil) return false;
  return ROTINAS_PAINEIS.some((r) => nivelRotina(acesso, r) >= NIVEIS.CONSULTAR);
}

export function podeAcessar(acesso, caminho) {
  if (!acesso?.perfil) return false;
  const regra = regraDaRota(caminho);
  if (regra?.livre) return true;
  if (regra?.prontuario) return podeVerProntuario(acesso);
  if (regra?.logAcesso) return podeVerLogAcesso(acesso);
  if (regra?.relatorios) return podeVerRelatorios(acesso);
  if (regra?.paineis) return podeVerPaineis(acesso);
  if (!regra) return acesso.perfil === PERFIS.ADMINISTRADOR;
  return nivelRotina(acesso, regra.rotina) >= NIVEIS.CONSULTAR;
}

/**
 * Tela inicial (src/docs/plano-front-atendimento.md, F-D2/F-D3/F-D4): administrador → painel; os demais → a
 * primeira tela de trabalho que o perfil acessa (agenda, pacientes, pacotes…); sem nenhuma → "Minha conta".
 * Profissional: "Hoje" (F1).
 */
export function telaInicial(acesso) {
  if (acesso?.perfil === PERFIS.ADMINISTRADOR) return '/';
  return INICIAIS.find((c) => podeAcessar(acesso, c)) || '/minha-conta';
}

/** Remove do menu o que o perfil não acessa (submenu vazio some junto). */
export function filtrarMenu(itens, acesso) {
  return (itens || [])
    .map((item) => {
      if (item.submenu) {
        const submenu = filtrarMenu(item.submenu, acesso);
        return submenu.length ? { ...item, submenu } : null;
      }
      return item.to && podeAcessar(acesso, item.to) ? item : null;
    })
    .filter(Boolean);
}

/** { nivel, podeVer, podeIncluir, podeAlterar, podeExcluir } — para mostrar/esconder botões. */
export function capacidades(acesso, rotina) {
  const nivel = nivelRotina(acesso, rotina);
  return {
    nivel,
    podeVer: nivel >= NIVEIS.CONSULTAR,
    podeIncluir: nivel >= NIVEIS.ALTERAR,
    podeAlterar: nivel >= NIVEIS.ALTERAR,
    podeExcluir: nivel >= NIVEIS.TOTAL,
  };
}
